import {
  futureValueLump,
  goalPeriodicContribution,
  monthlyEffectiveRate,
  presentValueLump,
  realAnnualRate,
} from '@fintracks/calculation-engine';
import type { ClientGoal, GoalFunding, ScenarioAssumptions } from '@fintracks/contracts';

const money = (value: number): string => Math.max(0, value).toFixed(2);

export function monthsUntilGoal(targetDate: string, assumptions: ScenarioAssumptions): number {
  const target = new Date(`${targetDate}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return 0;
  return Math.max(0, (target.getUTCFullYear() - assumptions.baseYear) * 12 + target.getUTCMonth() + 1 - assumptions.baseMonth);
}

export function calculateGoalFunding(goal: ClientGoal, assumptions: ScenarioAssumptions): GoalFunding {
  const months = monthsUntilGoal(goal.targetDate, assumptions);
  const annualRealRate = realAnnualRate(assumptions.nominalAnnualRatePct, assumptions.inflationAnnualRatePct);
  const monthlyRate = monthlyEffectiveRate(annualRealRate);
  const targetValue = Number(goal.targetValue);
  const reservedValue = Number(goal.reservedValue);
  const accumulatedAtGoal = futureValueLump(reservedValue, monthlyRate, months);
  const pendingAtGoal = targetValue - accumulatedAtGoal;
  const interval = goal.periodicity === 'annual' ? 12 : 1;

  if (goal.periodicity === 'one-time') {
    return {
      accumulatedAtGoal: money(accumulatedAtGoal),
      pendingAtGoal: money(pendingAtGoal),
      periodicContributionDue: '0.00',
      currentExcess: money(pendingAtGoal < 0 ? presentValueLump(-pendingAtGoal, monthlyRate, months) : 0),
      accumulationMonths: months,
      postAccumulationMonths: 0,
      contributionIntervalMonths: 1,
    };
  }

  if (months === 0) {
    return {
      accumulatedAtGoal: money(accumulatedAtGoal),
      pendingAtGoal: money(pendingAtGoal),
      periodicContributionDue: money(pendingAtGoal),
      currentExcess: money(pendingAtGoal < 0 ? -pendingAtGoal : 0),
      accumulationMonths: 0,
      postAccumulationMonths: 0,
      contributionIntervalMonths: interval,
    };
  }

  const contributionMonths = Math.floor(months / interval) * interval;
  const postAccumulationMonths = months - contributionMonths;
  if (contributionMonths === 0) {
    return {
      accumulatedAtGoal: money(accumulatedAtGoal),
      pendingAtGoal: money(pendingAtGoal),
      periodicContributionDue: money(pendingAtGoal > 0 ? presentValueLump(pendingAtGoal, monthlyRate, months) : 0),
      currentExcess: money(pendingAtGoal < 0 ? presentValueLump(-pendingAtGoal, monthlyRate, months) : 0),
      accumulationMonths: 0,
      postAccumulationMonths: months,
      contributionIntervalMonths: interval,
    };
  }

  const result = goalPeriodicContribution(
    targetValue,
    reservedValue,
    annualRealRate,
    contributionMonths,
    postAccumulationMonths,
    interval,
  );
  return {
    accumulatedAtGoal: result.accumulatedAtGoal.toFixed(2),
    pendingAtGoal: result.pendingAtGoal.toFixed(2),
    periodicContributionDue: result.periodicContributionDue.toFixed(2),
    currentExcess: result.currentExcess.toFixed(2),
    accumulationMonths: contributionMonths,
    postAccumulationMonths,
    contributionIntervalMonths: interval,
  };
}

export function goalProgress(goal: ClientGoal): number {
  const target = Number(goal.targetValue);
  return target <= 0 ? 0 : Math.min(100, Math.round(Number(goal.reservedValue) / target * 100));
}

export function goalPeriodLabel(goal: ClientGoal): string {
  if (goal.periodicity === 'annual') return 'aporte anual';
  if (goal.periodicity === 'one-time') return 'aporte único';
  return 'aporte mensal';
}
